import { memo, useCallback } from 'react';
import { Pressable } from 'react-native';

import { Stack } from '@/components/layout';
import { Avatar, SvgIcon, Text } from '@/components/ui';
import type { FamilyEvent } from '@/features/guardian/family-events';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

export type TimelineEventCardProps = {
  event: FamilyEvent;
  onPress: (event: FamilyEvent) => void;
  surface?: 'timeline' | 'modal';
};

/** One event block inside the day timeline, sized by the slot it is placed in. */
function TimelineEventCard({ event, onPress, surface = 'timeline' }: TimelineEventCardProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const handlePress = useCallback(() => onPress(event), [event, onPress]);
  const isCompact = surface === 'timeline' && event.endMinutes - event.startMinutes < 60;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${event.title} · ${event.playerName} · ${event.timeRangeLabel}`}
      onPress={handlePress}
      style={({ pressed }) => [
        styles.card,
        surface === 'modal' ? styles.modalCard : null,
        pressed ? styles.pressed : null,
      ]}>
      <Stack
        alignSelf="center"
        width={scale(3)}
        height={scale(isCompact ? 20 : 40)}
        borderRadius={scale(10)}
        marginLeft={scale(isCompact ? 4 : 8)}
        backgroundColor={event.accentColor}
      />

      <Stack
        flex={1}
        gap={scale(isCompact ? 1 : 2)}
        paddingLeft={scale(isCompact ? 4 : 8)}
        paddingRight={scale(isCompact ? 3 : 6)}
        paddingY={scale(isCompact ? 1 : 6)}
        style={styles.content}>
        <Text
          numberOfLines={1}
          variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
          style={styles.title}>
          {event.title}
        </Text>

        <Stack direction="row" align="center" gap={scale(isCompact ? 2 : 4)}>
          <Avatar size={scale(16)} borderWidth={0} activeOpacity={1}>
            <Avatar.Image source={event.playerAvatar} backgroundColor={guardianColors['2']} />
          </Avatar>
          <Text
            numberOfLines={1}
            variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
            style={styles.player}>
            {event.playerName}
          </Text>
        </Stack>

        {isCompact ? null : (
          <Stack direction="row" align="center" gap={scale(4)}>
            <SvgIcon name="IcClock" size={scale(12)} color={guardianColors['6']} />
            <Text
              numberOfLines={1}
              variant="Font Guardian App/Subtitle/Subtitle 11 Regular"
              style={styles.time}>
              {event.timeRangeLabel}
            </Text>
          </Stack>
        )}
      </Stack>
    </Pressable>
  );
}

const useStyles = createStyles(({ scale }) => ({
  card: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: scale(8),
    backgroundColor: colors['0'],
    shadowColor: '#D5D9E2',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.3,
    shadowRadius: 3,
    elevation: 2,
  },
  content: {
    minWidth: 0,
  },
  modalCard: {
    backgroundColor: guardianColors['1'],
    shadowColor: 'transparent',
    elevation: 0,
  },
  pressed: {
    opacity: 0.76,
  },
  title: {
    flexShrink: 1,
    color: guardianColors['Main/Main'],
  },
  player: {
    flexShrink: 1,
    color: guardianColors['10'],
  },
  time: {
    flexShrink: 1,
    color: guardianColors['6'],
  },
}));

export default memo(TimelineEventCard);
